import { Card } from '@/components/ui/Card';
import { barHeight } from '@/components/snapshot/snapshotCharts';
import { SnapshotChangePill } from '@/components/snapshot/SnapshotChangePill';
import {
  derivedForProduct,
  formatSnapshotCurrency,
  formatSnapshotPercent,
  type OrganicPaidBuckets,
  type ProductTotals,
} from '@/api/snapshotMetrics';

type BarMode = 'aov' | 'cr' | 'tacosAcos';

const EMPTY_PRODUCT: ProductTotals = {
  impressions: 0,
  clicks: 0,
  cost: 0,
  purchases30d: 0,
  sales30d: 0,
};

function SegmentBars({
  leftValue,
  rightValue,
  prevLeftValue,
  prevRightValue,
  leftLabel,
  rightLabel,
  format,
  leftColor,
  rightColor,
  maxHeight = 100,
}: {
  leftValue: number;
  rightValue: number;
  prevLeftValue: number;
  prevRightValue: number;
  leftLabel: string;
  rightLabel: string;
  format: (v: number) => string;
  leftColor: string;
  rightColor: string;
  maxHeight?: number;
}) {
  const max = Math.max(leftValue, rightValue, prevLeftValue, prevRightValue, 1);
  const bars = [
    {
      label: leftLabel,
      value: leftValue,
      prev: prevLeftValue,
      color: leftColor,
    },
    {
      label: rightLabel,
      value: rightValue,
      prev: prevRightValue,
      color: rightColor,
    },
  ];

  return (
    <div className="flex items-end justify-center gap-10 py-6">
      {bars.map((bar) => (
        <div key={bar.label} className="flex flex-col items-center gap-2">
          <div className="relative flex h-[110px] w-12 items-end justify-center">
            <div
              className="absolute bottom-0 w-8 rounded-t bg-muted/50"
              style={{ height: barHeight(bar.prev, max, maxHeight) }}
            />
            <div
              className={`relative w-8 rounded-t ${bar.color}`}
              style={{ height: barHeight(bar.value, max, maxHeight) }}
            />
          </div>
          <span className="text-[10px] font-medium text-foreground-muted">{bar.label}</span>
          <span className="text-xs font-semibold text-foreground">{format(bar.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** Category colors are fixed across all metric cards (not per-metric accents). */
const CATEGORY_THEME = {
  organic: { bar: 'bg-success', label: 'text-success' },
  paid: { bar: 'bg-brand', label: 'text-brand' },
  total: { bar: 'bg-violet-600', label: 'text-violet-700' },
} as const;

export function SnapshotBarCompareCard({
  title,
  mode,
  currentBuckets,
  previousBuckets,
  currentTotal,
  previousTotal,
  compareEnabled,
  bare = false,
  currentAcos = 0,
  previousAcos = 0,
}: {
  title: string;
  mode: BarMode;
  currentBuckets: OrganicPaidBuckets;
  previousBuckets: OrganicPaidBuckets;
  currentTotal: number;
  previousTotal: number;
  compareEnabled: boolean;
  bare?: boolean;
  currentAcos?: number;
  previousAcos?: number;
}) {
  const organic = currentBuckets.organic ?? EMPTY_PRODUCT;
  const paid = currentBuckets.paid ?? EMPTY_PRODUCT;
  const prevOrganic = previousBuckets.organic ?? EMPTY_PRODUCT;
  const prevPaid = previousBuckets.paid ?? EMPTY_PRODUCT;

  const isTacosAcos = mode === 'tacosAcos';
  const leftLabel = isTacosAcos ? 'Total' : 'Organic';
  const rightLabel = 'Paid';
  const leftTheme = isTacosAcos ? CATEGORY_THEME.total : CATEGORY_THEME.organic;
  const rightTheme = CATEGORY_THEME.paid;

  let format: (v: number) => string;
  let polarity: 'higher_is_better' | 'lower_is_better' = 'higher_is_better';
  let leftVal = 0;
  let rightVal = 0;
  let prevLeftVal = 0;
  let prevRightVal = 0;

  if (mode === 'aov') {
    format = formatSnapshotCurrency;
    leftVal = derivedForProduct(organic).aov;
    rightVal = derivedForProduct(paid).aov;
    prevLeftVal = derivedForProduct(prevOrganic).aov;
    prevRightVal = derivedForProduct(prevPaid).aov;
  } else if (mode === 'cr') {
    format = formatSnapshotPercent;
    leftVal = derivedForProduct(organic).cr;
    rightVal = derivedForProduct(paid).cr;
    prevLeftVal = derivedForProduct(prevOrganic).cr;
    prevRightVal = derivedForProduct(prevPaid).cr;
  } else {
    format = formatSnapshotPercent;
    polarity = 'lower_is_better';
    leftVal = currentTotal;
    rightVal = currentAcos;
    prevLeftVal = previousTotal;
    prevRightVal = previousAcos;
  }

  return (
    <Card className={bare ? 'border-0 shadow-none' : 'shadow-sm'}>
      <div className="flex flex-col p-5">
      <p className="text-sm font-medium text-foreground-muted">{title}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <p className="text-[1.75rem] font-bold leading-tight text-foreground">{format(currentTotal)}</p>
        <SnapshotChangePill
          current={currentTotal}
          previous={previousTotal}
          polarity={polarity}
          show={compareEnabled}
        />
      </div>
      {compareEnabled ? (
        <p className="mt-0.5 text-xs text-foreground-muted">Prev: {format(previousTotal)}</p>
      ) : null}
      <SegmentBars
        leftValue={leftVal}
        rightValue={rightVal}
        prevLeftValue={prevLeftVal}
        prevRightValue={prevRightVal}
        leftLabel={leftLabel}
        rightLabel={rightLabel}
        format={format}
        leftColor={leftTheme.bar}
        rightColor={rightTheme.bar}
      />
      <div className="grid grid-cols-2 gap-6 border-t border-border/60 pt-4 text-sm">
        <div>
          <p className={`font-semibold ${leftTheme.label}`}>{leftLabel}</p>
          <p className="mt-1 font-bold text-foreground">{format(leftVal)}</p>
        </div>
        <div>
          <p className={`font-semibold ${rightTheme.label}`}>{rightLabel}</p>
          <p className="mt-1 font-bold text-foreground">{format(rightVal)}</p>
        </div>
      </div>
      </div>
    </Card>
  );
}
